import { motion } from "framer-motion";
import {
	Circle,
	Target
} from "lucide-react";

import HelpPage from "@/components/dashboard/help-page";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createFileRoute, Link } from "@tanstack/react-router";

import { AppleButton, AppPromotionSection, Footer, GooglePlayButton, Header } from "./index";

export const Route = createFileRoute("/about")({
	component: AboutPage
});

function AboutPage() {
	return (
		<div className="bg-gradient-to-b from-white via-primary/10 to-violet-300">
			<motion.section
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.5 }}
				className="font-inter flex flex-col container mx-auto justify-between max-md:px-2"
			>
				<Header />

				<motion.section
					initial={{ opacity: 0, y: 50 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.3 }}
					className="flex flex-col md:flex-row gap-4 min-h-96 mt-40 bg-primary/90 rounded-3xl text-white max-md:mt-4"
				>
					<div className="flex-1 px-8 py-4">
						<motion.h1
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
							className="text-3xl leading-normal font-bold xl:max-w-sm max-md:text-2xl"
						>
							Protect Yourself from Fraudulent Vendors
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.4 }}
							className="opacity-80 mt-4 max-w-md max-md:text-sm"
						>
							You don't have to take a vendor's word for it. Simply ask them to pay through Intasida, and Intasida will act as a trusted intermediary to safeguard your transaction.

						</motion.p>

						<div className="flex gap-4 w-full max-md:items-center max-md:justify-center">
							<GooglePlayButton />
							<AppleButton />
						</div>
					</div>

					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.7, delay: 0.4 }}
						className="relative flex-1"
					>
						<img
							src="/mock-phones.png"
							alt="Home Hero"
							className="max-w-[clamp(20rem,60vw,36rem)] max-md:mx-auto max-md:relative lg:-top-32 lg:absolute"
						/>
					</motion.div>
				</motion.section>
				<motion.section
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="flex flex-col gap-4 items-center justify-center my-8"
				>
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						viewport={{ once: true }}
						className="text-4xl max-md:text-2xl font-bold"
					>
						Built on Strong Partnership
					</motion.h2>
					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						viewport={{ once: true }}
						className="text-gray-500 max-w-3xl max-md:text-center text-sm"
					>
						We're proud to work with a network of experts who share our vision.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.4 }}
						viewport={{ once: true }}
						className="flex gap-4"
					>
						<div className="flex items-center justify-center p-4">
							<img src="/v.png" alt="VFD" className="max-h-12 object-contain" />
						</div>
						<div className="flex items-center justify-center p-4">
							<img
								src="/safehaven.svg"
								alt="Safe Haven"
								className="max-h-16 object-contain"
							/>
						</div>
						<div className="flex items-center justify-center p-4">
							<img
								src="/peace-mass.png"
								alt="Peace Mass"
								className="max-h-32 object-contain"
							/>
						</div>
					</motion.div>
				</motion.section>

				<section
					id="about"
					className="flex flex-col gap-4 mt-10 items-center justify-center"
				>
					<h2 className="text-4xl max-md:text-2xl font-bold">
						Why Choose Intasida ?
					</h2>
					<p className="text-gray-500 max-w-3xl text-center text-sm">
						We are revolutionizing e-commerce by creating a business environment where integrity thrives between buyers and vendors.
					</p>
					<div className="flex gap-4">
						<div className="flex-1"></div>
						<div className="flex-1"></div>
					</div>
				</section>
				<div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
					<AboutFeatures />
					<div className="max-w-7xl mx-auto">

						<div className="text-center text-xl font-bold my-4">
							Commerce Made Easy With Intasida
						</div>

						<Tabs defaultValue="buyer">
							<TabsList className="grid w-full my-4 max-w-md mx-auto grid-cols-2 bg-transparent gap-4">
								<TabsTrigger
									className="border border-primary rounded-full text-primary data-[state=active]:bg-primary data-[state=active]:text-white h-9 px-4 py-2"
									value="buyer"
								>
									Buyer

								</TabsTrigger>
								<TabsTrigger
									className="border border-primary rounded-full text-primary data-[state=active]:bg-primary data-[state=active]:text-white h-9 px-4 py-2"
									value="vendor"
								>
									Vendor
								</TabsTrigger>
							</TabsList>
							<TabsContent value="buyer">
								<Card className="shadow-none max-md:p-0">
									<CardContent className="space-y-2">
										<div className="bg-white rounded-3xl p-8 max-md:p-1">
											<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
												<div>
													<h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 max-md:text-2xl max-md:my-4">
														Buying with Intasida
													</h2>

													<div className="space-y-8">
														{/* Step 1 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="size-6 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	1
																</div>
															</div>
															<div>
																<h3 className=" font-semibold text-gray-900 mb-2">
																	Get a Contract Code
																</h3>
																<p className="text-xs text-gray-600 leading-relaxed">
																	Get a contract code from your vendor or from
																	the intasida Telegram group.
																</p>
															</div>
														</div>

														{/* Step 2 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="size-6 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	2
																</div>
															</div>
															<div>
																<h3 className="font-semibold text-gray-900 mb-2">
																	Search the Code
																</h3>
																<p className="text-xs text-gray-600 leading-relaxed">
																	Enter the code on the intasida app
																</p>
															</div>
														</div>

														{/* Step 3 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="size-6 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	3
																</div>
															</div>
															<div>
																<h3 className="font-semibold text-gray-900 mb-2">
																	Review and Pay
																</h3>
																<p className="text-xs text-gray-600 leading-relaxed">
																	Review the item properly and make payment.
																</p>
															</div>
														</div>
														{/* Step 4 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="size-6 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	4
																</div>
															</div>
															<div>
																<h3 className="font-semibold text-gray-900 mb-2">
																	Track Your Order, Pickup and Confirm
																</h3>
																<p className="text-xs text-gray-600 leading-relaxed">
																	Track your orders seamlessly in order to follow up your delivery in the app, collect your items and confirm delivery.
																</p>
															</div>
														</div>
													</div>

													<Button className="rounded-full my-4" asChild>
														<Link to="/">Get Started For Free</Link>
													</Button>
												</div>

												<div className="relative">
													<img
														src="/app-showcase.png"
														alt="Smiling vendor in marketplace with leather goods"
														width={500}
														height={600}
														className="rounded-2xl object-cover w-full h-[500px] lg:h-[600px]"
													/>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</TabsContent>
							<TabsContent value="vendor">
								<Card className="shadow-none">
									<CardContent className="space-y-2">
										<div className="bg-white rounded-3xl p-8">
											<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
												<div className="relative">
													<img
														src="/iphone-photo.png"
														alt="Smiling vendor in marketplace with leather goods"
														width={500}
														height={600}
														className="rounded-2xl object-cover w-full h-[500px] lg:h-[600px]"
													/>
												</div>
												<div>
													<h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12">
														Selling with Intasida
													</h2>

													<div className="space-y-8">
														{/* Step 1 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	1
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Sign Up as a Vendor
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Create your Intasida account as a vendor
																</p>
															</div>
														</div>

														{/* Step 2 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	2
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	List Product
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Add the items you want to sell.
																</p>
															</div>
														</div>

														{/* Step 3 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	3
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Create a Contract ID
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Generate a contract using your product list.
																</p>
															</div>
														</div>
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	4
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Share and Send
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Send the contract ID to your buyer. they pay,
																	you drop off
																</p>
															</div>
														</div>
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	5
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Drop and Deliver
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Leave item at your nearest intasida location.
																	We handle the delivery
																</p>
															</div>
														</div>
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	6
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Get Paid Instantly
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Withdraw your money seamlessly from your
																	intasida wallet.
																</p>
															</div>
														</div>
													</div>

													<Button
														className="mt-8 bg-transparent text-primary hover:bg-purple-50 border border-primary px-6 py-2"
														asChild
													>
														<Link to="/vendor/home">Get Started</Link>
													</Button>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</TabsContent>
						</Tabs>
					</div>
				</div>

				<HelpPage />
				<AppPromotionSection />
			</motion.section>

			<Footer />
		</div>
	);
}

function AboutFeatures() {
	const features = [
		{
			title: "Secure Transactions",
			description:
        "End-to-end encryption and secure payment gateways to protect your financial data and ensure safe transactions.",
			bgColor: "bg-purple-100",
			iconBg: "bg-blue-600",
			icon: <Circle className="w-6 h-6 text-white fill-current" />,
			gridClass: "md:col-span-2 lg:col-span-2"
		},
		{
			title: "Secure Escrow Account",
			description:
        "Funds are held securely until contractual obligations and transaction are met.",
			bgColor: "bg-purple-200",
			iconBg: "bg-blue-600",
			icon: <Circle className="w-6 h-6 text-white fill-current" />,
			gridClass: "md:col-span-2"
		},
		{
			title: "Real Time Order Tracking",
			description:
        "Track your orders in real-time, including dispatch, transit, and delivery status directly from your buyer's dashboard",
			bgColor: "bg-white",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-1 md:row-span-1"
		},
		{
			title: "AI For Dispute Analysis",
			description:
        "Our AI feature automates the review process to speed up resolutions",
			bgColor: "bg-blue-700",
			iconBg: "bg-white",
			icon: <Circle className="w-6 h-6 text-blue-700 fill-current" />,
			textColor: "text-white",
			gridClass: "md:col-span-2 lg:col-span-2"
		},
		{
			title: "Integrated Logistics Network",
			description:
        "We integrated network of logistics agents to verify any product with contract terms before delivery to buyer.",
			bgColor: "bg-yellow-100",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-1"
		},
		{
			title: "Seamless Withdrawal",
			description:
        "Effortlessly withdraw your funds with easy steps directly to your bank mobile wallet within seconds.",
			bgColor: "bg-green-100",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-4"
		}
	];

	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			viewport={{ once: true }}
			className="p-6 max-w-7xl mx-auto max-md:p-0"
		>
			<section
				id="features"
				className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr my-6"
			>
				{features.map((feature, index) => (
					<motion.div
						key={feature.title}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.1 }}
						viewport={{ once: true }}
						className={`${feature.bgColor} ${feature.gridClass} rounded-2xl p-6 space-y-4 ${
							feature.textColor || "text-gray-900"
						} flex flex-col justify-between min-h-[200px]`}
					>
						<div className="space-y-4">
							<motion.div
								whileHover={{ scale: 1.1 }}
								transition={{ type: "spring", stiffness: 400, damping: 10 }}
								className={`${feature.iconBg} rounded-full w-12 h-12 flex items-center justify-center`}
							>
								{feature.icon}
							</motion.div>
							<div className="space-y-3">
								<h3 className="text-xl font-semibold leading-tight">
									{feature.title}
								</h3>
								<p
									className={`text-sm leading-relaxed ${feature.textColor ? "text-gray-200" : "text-gray-600"}`}
								>
									{feature.description}
								</p>
							</div>
						</div>
					</motion.div>
				))}
			</section>
		</motion.div>
	);
}
